import axios from 'src/utils/axios';
import * as Sentry from '@sentry/browser';
import { IBlankDrai } from 'src/interfaces/drai';

const baseURL = '/public/drai';

interface GetBlankDraiResponse {
  drai: IBlankDrai;
  message: string;
}

interface INewDraiUpdatePayload {
  approved: boolean;
  approvedById: number | null;
  approvedDate: string | null;
  approvedDateTimezone: string | null;
  caseId: number;
  createdBy: number;
  createdDate: string;
  deletedBy: number | null;
  deletedDate: string | null;
  eSignHistory: string | null;
  eSignUnsignReasonId: number | null;
  formId: number;
  formVersion: number;
  id: number;
  interviewDate: string;
  interviewDateTimezone: string;
  interviewerFirstName: string;
  interviewerLastName: string;
  interviewerFullName: string;
  interviewPlace: string;
  interviewees: Array<{ address: object; relationship: object }>;
  status: string;
  title: string;
  type: string;
  updatedBy: number;
  updatedDate: string;
  version: Record<string, number>;
  caseFormToUserCreatedBy: { id: number; entity: number };
  answers: Record<string, NewDraiAnswerBase>;
}

interface NewDraiAnswerBase {
  answer: string;
  answerTypeId: number;
  caseFormAnswerId: number;
}

class MedSocialService {
  /* DRAI */
  getDrai = async (caseId: string, draiId: string) => {
    try {
      const request = await axios.get(`${baseURL}/${caseId}/${draiId}`);
      if (request.status === 200) {
        return request.data.drai;
      }
      throw new Error(request.data.message);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  getDraiList = async (caseId: string) => {
    try {
      const request = await axios.get(`${baseURL}/${caseId}`);
      if (request.status === 200) {
        return request.data.drais;
      }
      throw new Error(request.data.message);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  createDrai = async (caseId: string, payload: INewDraiUpdatePayload) => {
    try {
      const request = await axios.post(`${baseURL}/${caseId}`, payload);
      if (request.status === 200) {
        return request.data.drai;
      }
      throw new Error(request.data.message);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  getBlankDrai = async ({ caseId, type }: { caseId: string; type: string }) => {
    try {
      const request = await axios.get<GetBlankDraiResponse>(
        `${baseURL}/${caseId}/blank/${type}`,
      );

      if (request.status === 200) {
        return request.data.drai;
      }
      throw new Error(request.data.message);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  updateDrai = async (
    caseId: string,
    draiId: string,
    payload: INewDraiUpdatePayload,
  ) => {
    try {
      const request = await axios.put(
        `${baseURL}/${caseId}/${draiId}`,
        payload,
      );
      if (request.status === 200) {
        return request.data.drai;
      }
      throw new Error(request.data.message);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  deleteDraiById = async (caseId: string, draiId: string) => {
    try {
      const request = await axios.delete(`${baseURL}/${caseId}/${draiId}`);
      if (request.status === 200) {
        return request;
      }
      throw new Error(request.data.message);
    } catch (error) {
      Sentry.captureException(error);
      return error;
    }
  };
}
const medSocialService = new MedSocialService();

export default medSocialService;
